/* DiceBear avatar — multi-style with per-style customization */
import React, { useMemo } from "react";

export const STYLES = [
  { id: "micah", label: "Komiks", emoji: "🎨" },
  { id: "adventurer", label: "Dobrodruh", emoji: "🗺️" },
  { id: "big-ears", label: "Velké uši", emoji: "👂" },
  { id: "big-smile", label: "Velký úsměv", emoji: "😄" },
  { id: "open-peeps", label: "Skicák", emoji: "✏️" },
  { id: "personas", label: "Postavy", emoji: "🎭" },
  { id: "toon-head", label: "Kreslené", emoji: "🦸" },
];

export const HAIR_COLORS = ["000000", "2c1b18", "77311d", "b58143", "d6b370", "e5d7a3", "ffb347", "ff5d8f", "8c52ff", "22d3ee", "ffffff"];
export const SKIN_COLORS = ["f9c9b6", "edb98a", "d08b5b", "ae5d29", "77311d"];
export const HAIR_STYLES = ["fonze", "mrT", "mrClean", "dannyPhantom", "full", "turban", "dougFunny", "pixie"];
export const EYES = ["eyes", "eyesShadow", "round", "smiling", "smilingShadow"];
export const MOUTHS = ["frown", "laughing", "nervous", "pucker", "sad", "smile", "smirk", "surprised"];
export const GLASSES = ["none", "round", "square"];
export const EARRINGS = ["none", "hoop", "stud"];
export const SHIRT = ["collared", "crew", "open"];
export const BG = ["FFD5DC", "C0AEFF", "FEF08A", "BFDBFE", "FBCFE8", "A7F3D0", "FDBA74", "22D3EE", "FACC15"];

const PALETTE = ["FF4500", "22D3EE", "F43F5E", "10B981", "FACC15", "2563EB", "FDE047", "C0AEFF"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randSeed = () => Math.random().toString(36).slice(2, 10);

export function randomAvatarConfig(style = "micah") {
  const base = { style, seed: randSeed(), bg: pick(BG) };
  if (style === "micah") {
    return {
      ...base,
      hair: pick(HAIR_STYLES),
      hairColor: pick(HAIR_COLORS),
      skinColor: pick(SKIN_COLORS),
      eyes: pick(EYES),
      mouth: pick(MOUTHS),
      glasses: pick(GLASSES),
      earrings: pick(EARRINGS),
      shirt: pick(SHIRT),
      shirtColor: pick(PALETTE),
    };
  }
  // For other styles we keep the API simple: seed + background. Each re-roll
  // gives a fresh randomized character courtesy of DiceBear's deterministic seeding.
  return base;
}

export function buildAvatarUrl(cfg, size = 220) {
  if (!cfg) cfg = randomAvatarConfig();
  const style = cfg.style || "micah";
  const params = new URLSearchParams();
  params.set("seed", cfg.seed || "dm");
  params.set("size", size);
  params.set("backgroundColor", cfg.bg || "FACC15");

  if (style === "micah") {
    if (cfg.hair && cfg.hair !== "none") params.set("hair", cfg.hair);
    if (cfg.hairColor) params.set("hairColor", cfg.hairColor);
    if (cfg.skinColor) params.set("baseColor", cfg.skinColor);
    if (cfg.eyes) params.set("eyes", cfg.eyes);
    if (cfg.mouth) params.set("mouth", cfg.mouth);
    if (cfg.glasses && cfg.glasses !== "none") {
      params.set("glasses", cfg.glasses);
      params.set("glassesProbability", "100");
    } else {
      params.set("glassesProbability", "0");
    }
    if (cfg.earrings && cfg.earrings !== "none") {
      params.set("earrings", cfg.earrings);
      params.set("earringsProbability", "100");
    } else {
      params.set("earringsProbability", "0");
    }
    if (cfg.shirt) params.set("shirt", cfg.shirt);
    if (cfg.shirtColor) params.set("shirtColor", cfg.shirtColor);
  }

  return `https://api.dicebear.com/9.x/${style}/svg?${params.toString()}`;
}

export function Avatar({ config, size = 96, className = "" }) {
  const url = useMemo(() => buildAvatarUrl(config, size), [config, size]);
  return (
    <img
      src={url}
      alt="avatar"
      width={size}
      height={size}
      className={className}
      style={{ display: "block" }}
      data-testid="dicebear-avatar"
    />
  );
}

/* Full builder UI — step 1: style picker, step 2: customise */
export function AvatarBuilder({ value, onChange }) {
  const cfg = value || randomAvatarConfig();
  const style = cfg.style || "micah";
  const update = (key, v) => onChange({ ...cfg, [key]: v });
  const switchStyle = (newStyle) => onChange(randomAvatarConfig(newStyle));

  const Section = ({ title, options, active, onPick, renderOption }) => (
    <div className="space-y-2">
      <div className="text-xs font-black uppercase tracking-wider text-black/60">{title}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const key = typeof o === "string" ? o : o.value;
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onPick(key)}
              data-testid={`avatar-opt-${title.replace(/\s/g, "-").toLowerCase()}-${key}`}
              className={`border-2 border-black rounded-xl px-3 py-2 text-xs font-bold transition-all ${
                isActive ? "bg-black text-white shadow-brut-sm" : "bg-white hover:-translate-y-[1px] hover:shadow-brut-sm"
              }`}
              style={renderOption?.(key) || {}}
            >
              {typeof o === "string" ? o : o.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
      {/* Preview column */}
      <div className="brut-card p-4 flex flex-col items-center gap-3 sticky top-4 h-fit">
        <div
          className="w-60 h-60 rounded-xl overflow-hidden border-2 border-black"
          style={{ background: `#${cfg.bg}` }}
        >
          <Avatar config={cfg} size={240} />
        </div>
        <button
          type="button"
          onClick={() => onChange({ ...randomAvatarConfig(style) })}
          className="brut-btn bg-brand-yellow text-black w-full"
          data-testid="avatar-randomize"
        >
          🎲 Zamíchat
        </button>
        <div className="text-[11px] text-black/60 text-center">
          Styl: <strong>{STYLES.find((s) => s.id === style)?.label || style}</strong>
        </div>
      </div>

      {/* Customisation column */}
      <div className="space-y-6">
        {/* Step 1: Style picker — preview each style with same seed */}
        <div>
          <div className="text-xs font-black uppercase tracking-wider text-black/60 mb-2">
            1. Vyber styl avatara
          </div>
          <div className="flex flex-wrap gap-3" data-testid="avatar-style-grid">
            {STYLES.map((s) => {
              const previewCfg = { ...cfg, style: s.id };
              const isActive = style === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => switchStyle(s.id)}
                  data-testid={`avatar-style-${s.id}`}
                  className={`w-[92px] border-2 border-black rounded-xl p-2 flex flex-col items-center gap-1.5 transition-all flex-shrink-0 ${
                    isActive ? "bg-black text-white shadow-brut-sm" : "bg-white hover:-translate-y-[1px] hover:shadow-brut-sm"
                  }`}
                  title={s.label}
                >
                  <div
                    className="w-16 h-16 rounded-md overflow-hidden border border-black/10 flex-shrink-0"
                    style={{ background: `#${cfg.bg}` }}
                  >
                    <Avatar config={previewCfg} size={64} />
                  </div>
                  <div className="text-[10px] font-bold leading-tight text-center break-words w-full">
                    {s.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Per-style controls */}
        <div>
          <div className="text-xs font-black uppercase tracking-wider text-black/60 mb-2">
            2. Vlastní úpravy
          </div>

          {style === "micah" ? (
            <div className="space-y-5">
              <Section title="Účes" options={HAIR_STYLES} active={cfg.hair} onPick={(v) => update("hair", v)} />
              <Section
                title="Barva vlasů"
                options={HAIR_COLORS}
                active={cfg.hairColor}
                onPick={(v) => update("hairColor", v)}
                renderOption={(v) => ({ backgroundColor: `#${v}`, color: "#fff", minWidth: 38, textShadow: "0 1px 2px rgba(0,0,0,.5)" })}
              />
              <Section
                title="Pleť"
                options={SKIN_COLORS}
                active={cfg.skinColor}
                onPick={(v) => update("skinColor", v)}
                renderOption={(v) => ({ backgroundColor: `#${v}`, color: "#111", minWidth: 38 })}
              />
              <Section title="Oči" options={EYES} active={cfg.eyes} onPick={(v) => update("eyes", v)} />
              <Section title="Ústa" options={MOUTHS} active={cfg.mouth} onPick={(v) => update("mouth", v)} />
              <Section title="Brýle" options={GLASSES} active={cfg.glasses} onPick={(v) => update("glasses", v)} />
              <Section title="Náušnice" options={EARRINGS} active={cfg.earrings} onPick={(v) => update("earrings", v)} />
              <Section title="Top" options={SHIRT} active={cfg.shirt} onPick={(v) => update("shirt", v)} />
              <Section
                title="Barva trika"
                options={PALETTE}
                active={cfg.shirtColor}
                onPick={(v) => update("shirtColor", v)}
                renderOption={(v) => ({ backgroundColor: `#${v}`, color: "#111", minWidth: 38 })}
              />
              <Section
                title="Pozadí"
                options={BG}
                active={cfg.bg}
                onPick={(v) => update("bg", v)}
                renderOption={(v) => ({ backgroundColor: `#${v}`, color: "#111", minWidth: 38 })}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-black/60">
                U tohoto stylu změníš celý vzhled tlačítkem <strong>Zamíchat</strong>.
                Můžeš si vybrat barvu pozadí.
              </p>
              <Section
                title="Pozadí"
                options={BG}
                active={cfg.bg}
                onPick={(v) => update("bg", v)}
                renderOption={(v) => ({ backgroundColor: `#${v}`, color: "#111", minWidth: 38 })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
